// ---------------------------------------------------------------------------
// /api/config, fetched once per page load and shared.
//
// It carries whether Turnstile and Clerk are configured at all. Both are
// optional: a local run has neither, and the site has to work without them
// rather than showing a sign-in button that cannot possibly succeed.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { getConfig, type SiteConfig } from '../lib/api.js';
import { useGameStore } from '../store/gameStore.js';

let cached: SiteConfig | null = null;
let inFlight: Promise<SiteConfig> | null = null;

/**
 * Put an announced restart back on screen after a reload.
 *
 * The announcement is a socket broadcast to rooms, so it reached only people
 * already sitting in one and died the moment they refreshed. The config carries
 * it now, and this hands it to the store through the same action the socket
 * uses — so there is still one place a pending restart lives, and no second
 * notion of what the player has been told.
 *
 * Done at the fetch rather than in a component, because the fetch happens once
 * per page load and every screen shares its result.
 */
function adoptMaintenance(config: SiteConfig): void {
  const notice = config.maintenance;
  if (notice === undefined || notice === null) return;

  // A live socket has the newer picture: it hears the announcement and hears
  // nothing more once the restart has happened. Do not overwrite that with a
  // config response fetched at some unknown point before now.
  //
  // The one exception is the announcement gaining a deadline it did not have.
  // CI announces a deploy with no landing time, and the process being replaced
  // announces the exact one later — over a socket, which nobody sitting on the
  // home screen has. Without this they would keep the vague card through a
  // cutover they could have been counted down to.
  const held = useGameStore.getState().maintenance;
  const gainedADeadline = held !== null && held.deployAt === null && notice.deployAt !== null;
  if (held !== null && !(gainedADeadline && held.updateComplete !== true)) return;

  useGameStore.getState().announceMaintenance(notice);
}

/**
 * How often to ask `/api/config` whether the build has changed.
 *
 * Idle, this is a background curiosity. With a deploy announced it is the only
 * thing that will notice the new process, and the whole point of the exercise
 * is that the moment is exact — so it goes to a few seconds for as long as a
 * restart is outstanding, and back to a minute once it has landed.
 */
const IDLE_POLL_MS = 60_000;
const DEPLOY_POLL_MS = 3_000;

function pollDelayMs(): number {
  const notice = useGameStore.getState().maintenance;
  if (notice === null || notice.updateComplete === true) return IDLE_POLL_MS;
  return DEPLOY_POLL_MS;
}

function fetchOnce(): Promise<SiteConfig> {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = getConfig()
    .then((config) => {
      cached = config;
      adoptMaintenance(config);
      return config;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

export interface SiteConfigState {
  config: SiteConfig | null;
  /** True until the first response, so callers do not act on a false default. */
  loading: boolean;
  error: string | null;
}

export function useSiteConfig(): SiteConfigState {
  const [state, setState] = useState<SiteConfigState>(() => ({
    config: cached,
    loading: cached === null,
    error: null,
  }));

  useEffect(() => {
    let live = true;

    fetchOnce()
      .then((config) => {
        if (live) setState({ config, loading: false, error: null });
      })
      .catch((error: unknown) => {
        if (!live) return;
        setState({
          config: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Could not reach the server.',
        });
      });

    const checkBuildId = () => {
      getConfig()
        .then((newConfig) => {
          adoptMaintenance(newConfig);
          // The one piece of evidence that the deploy landed: this response came
          // from a process built from different source than the one this page
          // loaded against. A reconnect proves nothing — see the note in the
          // store — and neither does the announced moment simply passing.
          if (cached && newConfig.buildId && cached.buildId && newConfig.buildId !== cached.buildId) {
            useGameStore.getState().completeUpdate();
          }
          cached = newConfig;
          if (live) setState({ config: newConfig, loading: false, error: null });
        })
        .catch(() => {});
    };

    // A self-rescheduling timeout rather than an interval, because the cadence
    // is not fixed: it tightens while a restart is outstanding.
    let timer = 0;
    const schedule = () => {
      if (!live) return;
      timer = window.setTimeout(() => {
        checkBuildId();
        schedule();
      }, pollDelayMs());
    };

    schedule();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkBuildId();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      live = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return state;
}

/**
 * A Turnstile token for a create or join attempt, or undefined when bot
 * protection is switched off. Throws with a readable reason when the challenge
 * itself fails, so a caller can show it rather than silently sending nothing.
 */
export async function acquireTurnstileToken(config: SiteConfig | null): Promise<string | undefined> {
  if (!config?.turnstileEnabled || !config.turnstileSiteKey) return undefined;
  const { getTurnstileToken } = await import('../lib/turnstile.js');
  return getTurnstileToken(config.turnstileSiteKey);
}
